import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
} from '@mui/material';
import { CHANGELOG, ChangelogEntryType } from './changelog';

interface ChangelogModalProps {
  open: boolean;
  onClose: () => void;
}

/** Mapping von Entry-Typ zu Emoji und Label */
const entryTypeConfig: Record<ChangelogEntryType, { emoji: string; label: string; color: string }> =
  {
    feature: { emoji: '🚀', label: 'Feature', color: '#2e7d32' },
    fix: { emoji: '🐛', label: 'Fix', color: '#ed6c02' },
    improvement: { emoji: '✨', label: 'Verbesserung', color: '#0288d1' },
  };

/**
 * Modal-Dialog, der die neueste Changelog-Version anzeigt.
 * Wird einmalig nach einem App-Update eingeblendet.
 */
const ChangelogModal: React.FC<ChangelogModalProps> = ({ open, onClose }) => {
  // Immer nur die neueste Version anzeigen
  const latestVersion = CHANGELOG[0];

  if (!latestVersion) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="h6" component="span" fontWeight={700}>
            Was ist neu?
          </Typography>
          <Chip
            label={`v${latestVersion.version}`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>
        <Typography variant="body2" color="text.secondary">
          {latestVersion.title} &mdash; {latestVersion.date}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <List disablePadding>
          {latestVersion.entries.map((entry, index) => {
            const config = entryTypeConfig[entry.type];
            return (
              <ListItem
                key={index}
                alignItems="flex-start"
                sx={{
                  px: 0,
                  py: 1,
                  gap: 1.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 'auto',
                    mt: 0.5,
                    fontSize: '1.25rem',
                  }}
                >
                  {config.emoji}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                      <Chip
                        label={config.label}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          bgcolor: `${config.color}14`,
                          color: config.color,
                          border: `1px solid ${config.color}40`,
                        }}
                      />
                    </Box>
                  }
                  secondary={
                    <Typography variant="body2" color="text.primary" sx={{ mt: 0.25 }}>
                      {entry.description}
                    </Typography>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained" fullWidth>
          Verstanden
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangelogModal;
